import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { imgUrl, formatINR } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { X, Heart, MessageCircle, MapPin, BedDouble, Bath, Loader2, LogIn, Sparkles } from "lucide-react";
import { toast } from "sonner";

function Card({ p, style, drag, onDragEnd, mv }) {
  const rotate = useTransform(mv, [-200, 200], [-14, 14]);
  const likeOp = useTransform(mv, [30, 140], [0, 1]);
  const nopeOp = useTransform(mv, [-30, -140], [0, 1]);
  return (
    <motion.div
      className="absolute inset-0"
      style={drag ? { x: mv, rotate } : style}
      drag={drag ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={onDragEnd}
      whileTap={{ cursor: "grabbing" }}
    >
      <div className="h-full w-full rounded-[28px] overflow-hidden bg-white shadow-[0_20px_50px_-12px_rgba(255,107,91,0.4)] border border-[#ffe3db] select-none">
        <div className="relative h-[62%]">
          <img src={imgUrl(p.images?.[0])} alt={p.title} className="h-full w-full object-cover pointer-events-none" draggable={false} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          {drag && (
            <>
              <motion.div style={{ opacity: likeOp }} className="absolute top-6 left-6 rotate-[-12deg] border-4 border-[#22c55e] text-[#22c55e] font-black text-2xl px-3 py-1 rounded-xl">LIKE</motion.div>
              <motion.div style={{ opacity: nopeOp }} className="absolute top-6 right-6 rotate-[12deg] border-4 border-[#ff5b5b] text-[#ff5b5b] font-black text-2xl px-3 py-1 rounded-xl">PASS</motion.div>
            </>
          )}
          <span className="absolute top-4 left-4 bg-[#ff6b5b] text-white text-xs font-bold px-3 py-1 rounded-full capitalize">For {p.listing_type}</span>
          <div className="absolute bottom-4 left-5 right-5 text-white">
            <p className="font-display text-3xl font-extrabold drop-shadow">{formatINR(p.price)}{p.listing_type === "rent" && <span className="text-base font-medium">/mo</span>}</p>
            <p className="mt-1 font-semibold text-lg leading-tight drop-shadow line-clamp-1">{p.title}</p>
          </div>
        </div>
        <div className="h-[38%] p-5">
          <p className="flex items-center gap-1.5 text-slate-500 text-sm"><MapPin className="h-4 w-4 text-[#ff6b5b]" /> {p.locality}, {p.city}</p>
          <div className="mt-3 flex items-center gap-4 text-slate-700 font-semibold">
            <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-[#ff6b5b]" /> {p.bhk} BHK</span>
            <span className="flex items-center gap-1.5"><Bath className="h-4 w-4 text-[#ff6b5b]" /> {p.bathrooms} Bath</span>
            <span className="text-slate-400 font-medium">{p.area_sqft} sqft</span>
          </div>
          <p className="mt-3 text-sm text-slate-500 line-clamp-2">{p.description}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function DiscoverSwipe() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [deck, setDeck] = useState([]);
  const [loading, setLoading] = useState(true);
  const mv = useMotionValue(0);

  const load = () => {
    setLoading(true);
    api.get("/swipe/deck").then((r) => setDeck(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const act = async (direction) => {
    const top = deck[0];
    if (!top) return;
    mv.set(0);
    setDeck((d) => d.slice(1));
    try {
      await api.post("/swipe", { property_id: top.id, direction });
      if (direction === "like") toast.success("Shortlisted! 💛 Say hi to the owner from your Likes.", { duration: 2500 });
    } catch { /* ignore */ }
  };

  const onDragEnd = (_e, info) => {
    if (info.offset.x > 120) act("like");
    else if (info.offset.x < -120) act("pass");
    else mv.set(0);
  };

  const sayHi = async () => {
    const top = deck[0];
    if (!top) return;
    try {
      const r = await api.post("/messages/thread", { property_id: top.id });
      await api.post("/swipe", { property_id: top.id, direction: "like" }).catch(() => {});
      setDeck((d) => d.slice(1));
      navigate(`/chats/${r.data.id}`);
    } catch { toast.error("Could not start chat"); }
  };

  if (!user) {
    return (
      <div className="min-h-[80vh] bg-[#fff7f0] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="h-16 w-16 rounded-3xl bg-[#ff6b5b] flex items-center justify-center mx-auto text-white"><Sparkles className="h-8 w-8" /></div>
          <h1 className="mt-5 font-display text-3xl font-extrabold text-slate-900">Find your people.<br />Then find a place.</h1>
          <p className="mt-3 text-slate-500">Swipe through homes, match with the ones you love, and chat — renting made fun, not stressful.</p>
          <Button data-testid="discover-login-btn" onClick={login} className="mt-6 rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white px-8 h-12 text-base"><LogIn className="h-4 w-4 mr-2" /> Sign in to start swiping</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[88vh] bg-[#fff7f0]" data-testid="discover-page">
      <div className="max-w-md mx-auto px-5 pt-8 pb-28">
        <div className="text-center">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Meet your next home 🏡</h1>
          <p className="mt-1.5 text-slate-500">Swipe right to like, left to pass. <span className="text-[#ff6b5b] font-semibold">Matches, not maybes.</span></p>
        </div>

        <div className="relative mt-6 h-[520px]" data-testid="swipe-deck">
          {loading ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#ff6b5b]" /></div>
          ) : deck.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center bg-white rounded-[28px] border border-[#ffe3db] p-8">
              <div className="h-14 w-14 rounded-full bg-[#fff0eb] flex items-center justify-center text-[#ff6b5b] text-2xl">🎉</div>
              <p className="mt-4 font-display text-xl font-bold text-slate-900">You're all caught up!</p>
              <p className="mt-1 text-slate-500 text-sm">Check your likes or come back for fresh homes.</p>
              <div className="mt-5 flex gap-2">
                <Button onClick={() => navigate("/chats")} className="rounded-full bg-[#ff6b5b] hover:bg-[#f2523f] text-white">Your chats</Button>
                <Button onClick={load} variant="outline" className="rounded-full border-[#ffcabf] text-[#ff6b5b]">Refresh</Button>
              </div>
            </div>
          ) : (
            <AnimatePresence>
              {deck.slice(0, 3).reverse().map((p, idx, arr) => {
                const isTop = idx === arr.length - 1;
                const depth = arr.length - 1 - idx;
                return isTop ? (
                  <Card key={p.id} p={p} drag mv={mv} onDragEnd={onDragEnd} />
                ) : (
                  <Card key={p.id} p={p} style={{ scale: 1 - depth * 0.04, y: depth * 14 }} />
                );
              })}
            </AnimatePresence>
          )}
        </div>

        {!loading && deck.length > 0 && (
          <div className="mt-6 flex items-center justify-center gap-5">
            <button data-testid="swipe-pass-btn" onClick={() => act("pass")} className="h-16 w-16 rounded-full bg-white border-2 border-slate-200 text-[#ff5b5b] flex items-center justify-center shadow-lg hover:scale-105 transition-transform"><X className="h-7 w-7" /></button>
            <button data-testid="swipe-hi-btn" onClick={sayHi} className="h-14 w-14 rounded-full bg-white border-2 border-[#ffcabf] text-[#ff6b5b] flex items-center justify-center shadow-lg hover:scale-105 transition-transform"><MessageCircle className="h-6 w-6" /></button>
            <button data-testid="swipe-like-btn" onClick={() => act("like")} className="h-16 w-16 rounded-full bg-[#ff6b5b] text-white flex items-center justify-center shadow-lg hover:scale-105 transition-transform"><Heart className="h-7 w-7 fill-white" /></button>
          </div>
        )}
      </div>
    </div>
  );
}
